import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// WebSocket connection states
export const CONNECTION_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

// Message types
export const MESSAGE_TYPES = {
  USER: 'user',
  AI: 'ai',
  SYSTEM: 'system',
  TYPING: 'typing',
};

// Initial state
const initialState = {
  // Messages
  messages: [],

  // Connection state
  connectionState: CONNECTION_STATES.DISCONNECTED,
  connectionError: null,

  // UI state
  isTyping: false,
  isSending: false,

  // WebSocket
  wsConnection: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,

  // Error handling
  error: null,
  lastError: null,
};

// Async thunks
export const sendMessage = createAsyncThunk('chat/sendMessage', async (messageData, { rejectWithValue, getState }) => {
  try {
    const { message, timestamp } = messageData;

    // For now, simulate API call
    // TODO: Replace with actual WebSocket or API call
    const response = await new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: {
            success: true,
            message: `Bu bir test yanıtıdır. Sorunuz: "${message}"`,
            timestamp: new Date().toISOString(),
          },
        });
      }, 2000);
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.message || 'Mesaj gönderilemedi');
  }
});

export const connectWebSocket = createAsyncThunk('chat/connectWebSocket', async (_, { rejectWithValue, dispatch }) => {
  try {
    // TODO: Implement actual WebSocket connection
    // const ws = new WebSocket('wss://your-websocket-url');

    // For now, simulate connection
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ connected: true });
      }, 1000);
    });
  } catch (error) {
    return rejectWithValue(error.message || 'WebSocket bağlantısı kurulamadı');
  }
});

export const disconnectWebSocket = createAsyncThunk('chat/disconnectWebSocket', async (_, { getState }) => {
  const { wsConnection } = getState().chat;

  if (wsConnection) {
    // wsConnection.close();
  }

  return { disconnected: true };
});

// Chat slice
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Message actions
    addMessage: (state, action) => {
      const { type, text, timestamp, id } = action.payload;
      state.messages.push({
        id: id || Date.now(),
        type,
        text,
        timestamp: timestamp || new Date(),
      });
    },

    clearMessages: (state) => {
      state.messages = [];
    },

    updateMessage: (state, action) => {
      const { id, updates } = action.payload;
      const messageIndex = state.messages.findIndex((msg) => msg.id === id);
      if (messageIndex !== -1) {
        state.messages[messageIndex] = { ...state.messages[messageIndex], ...updates };
      }
    },

    removeMessage: (state, action) => {
      const messageId = action.payload;
      state.messages = state.messages.filter((msg) => msg.id !== messageId);
    },

    // Typing actions
    setTyping: (state, action) => {
      state.isTyping = action.payload;
    },

    setSending: (state, action) => {
      state.isSending = action.payload;
    },

    // Connection actions
    setConnectionState: (state, action) => {
      state.connectionState = action.payload;
    },

    setConnectionError: (state, action) => {
      state.connectionError = action.payload;
    },

    setReconnectAttempts: (state, action) => {
      state.reconnectAttempts = action.payload;
    },

    // Error actions
    setError: (state, action) => {
      state.error = action.payload;
      state.lastError = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // WebSocket actions
    setWebSocketConnection: (state, action) => {
      state.wsConnection = action.payload;
    },

    // Initialize chat with welcome message
    initializeChat: (state, action) => {
      const { welcomeMessage } = action.payload;
      if (state.messages.length === 0) {
        state.messages.push({
          id: Date.now(),
          type: MESSAGE_TYPES.AI,
          text: welcomeMessage,
          timestamp: new Date(),
        });
      }
    },
  },
  extraReducers: (builder) => {
    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.isTyping = false;

        // Add AI response
        state.messages.push({
          id: Date.now(),
          type: MESSAGE_TYPES.AI,
          text: action.payload.message,
          timestamp: new Date(action.payload.timestamp),
        });
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        state.isTyping = false;
        state.error = action.payload;
      });

    // Connect WebSocket
    builder
      .addCase(connectWebSocket.pending, (state) => {
        state.connectionState = CONNECTION_STATES.CONNECTING;
        state.connectionError = null;
      })
      .addCase(connectWebSocket.fulfilled, (state) => {
        state.connectionState = CONNECTION_STATES.CONNECTED;
        state.reconnectAttempts = 0;
        state.connectionError = null;
      })
      .addCase(connectWebSocket.rejected, (state, action) => {
        state.connectionState = CONNECTION_STATES.ERROR;
        state.connectionError = action.payload;
      });

    // Disconnect WebSocket
    builder.addCase(disconnectWebSocket.fulfilled, (state) => {
      state.connectionState = CONNECTION_STATES.DISCONNECTED;
      state.wsConnection = null;
      state.connectionError = null;
    });
  },
});

// Export actions
export const {
  addMessage,
  clearMessages,
  updateMessage,
  removeMessage,
  setTyping,
  setSending,
  setConnectionState,
  setConnectionError,
  setReconnectAttempts,
  setError,
  clearError,
  setWebSocketConnection,
  initializeChat,
} = chatSlice.actions;

// Selectors
export const selectMessages = (state) => state.chat.messages;
export const selectConnectionState = (state) => state.chat.connectionState;
export const selectIsTyping = (state) => state.chat.isTyping;
export const selectIsSending = (state) => state.chat.isSending;
export const selectConnectionError = (state) => state.chat.connectionError;
export const selectError = (state) => state.chat.error;
export const selectReconnectAttempts = (state) => state.chat.reconnectAttempts;

export default chatSlice.reducer;
